/**
 * API configuration for the Dashboard app.
 * Uses the scheduler-lib API client with dashboard-specific configuration.
 */

// API base URL
// - Uses VITE_API_URL if set
// - Falls back to localhost:8787 in development
// - Falls back to same-origin (empty string) in production
const API_BASE_URL =
  import.meta.env.VITE_API_URL ??
  (import.meta.env.DEV ? 'http://localhost:8787' : '');

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

/**
 * Custom error class for API errors.
 */
export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Make an API request with proper headers for development.
 * In production, Cloudflare Access handles auth via cookies.
 * In development, we use X-Mock-User-Role header.
 */
async function request<T>(
  endpoint: string,
  { params, ...init }: RequestOptions = {}
): Promise<T> {
  const url = new URL(`/api/v1${endpoint}`, API_BASE_URL);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  const response = await fetch(url.toString(), {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      // Development mock auth header
      'X-Mock-User-Role': 'leadership',
      ...init.headers,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    // Read response text first, then try to parse as JSON
    const responseText = await response.text();
    let errorCode = 'UNKNOWN_ERROR';
    let errorMessage = `API error: ${response.status}`;

    try {
      const error = JSON.parse(responseText);
      errorCode = error.error?.code ?? errorCode;
      errorMessage = error.error?.message ?? errorMessage;
    } catch {
      // JSON parse failed - use response text if available, otherwise statusText
      errorMessage = responseText || response.statusText || errorMessage;
    }

    throw new ApiError(errorCode, errorMessage, response.status);
  }

  return response.json();
}

export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'GET' }),
  post: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),
};
